import React from 'react';
import { Button } from './Button';

// DecisionFeedback - the result of a decision, shown on the card or row it was made on
// (V2-013, V2-014; DESIGN.md STATE-06, STATE-09).
//
// Approve, Send for Revisions, Decline, Withdraw (Submissions) and Approve / Reject / Activate
// (Vendor Approvals) show their result here, without a page reload: the new status and a short
// message, announced to screen readers. The list keeps the card for about 5 seconds, then it
// leaves. A failure stays on the card with the reason and Retry. A toast may accompany this,
// never replace it.

export const DECISION_FEEDBACK_MS = 5000;

export interface DecisionResult {
  tone: 'success' | 'error';
  message: string;
  /** Error only: runs the same decision again. */
  onRetry?: () => void;
  /** Optional extra line, e.g. a ticket number with a Copy action. */
  detail?: React.ReactNode;
}

export interface DecisionFeedbackProps {
  result: DecisionResult;
  className?: string;
}

export const DecisionFeedback: React.FC<DecisionFeedbackProps> = ({ result, className = '' }) => {
  const success = result.tone === 'success';
  return (
    <div
      role={success ? 'status' : 'alert'}
      aria-live={success ? 'polite' : 'assertive'}
      data-testid="decision-feedback"
      data-tone={result.tone}
      className={`tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded tw-border tw-px-3 tw-py-2 tw-text-sm ${
        success
          ? 'tw-border-green-600 tw-bg-green-50 tw-text-green-800'
          : 'tw-border-red-600 tw-bg-red-50 tw-text-red-800'
      } ${className}`}
    >
      <i className={`bi ${success ? 'bi-check-circle' : 'bi-exclamation-triangle'}`} aria-hidden="true" />
      <span className="tw-font-semibold">{result.message}</span>
      {result.detail}
      {!success && result.onRetry && (
        <Button type="button" size="sm" variant="outline-secondary" className="tw-ml-auto" onClick={result.onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
};

export default DecisionFeedback;
